import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

// Pricing plans data
const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    period: 'month',
    description: 'Perfect for small lending operations',
    features: [
      'Up to 50 clients',
      'Basic risk assessment',
      'Email support',
      '5 AI summaries/month',
      '1 user seat'
    ],
    popular: false
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 149,
    period: 'month',
    description: 'For growing fintech companies',
    features: [
      'Up to 500 clients',
      'Advanced risk scoring',
      'Priority support',
      '50 AI summaries/month',
      '5 user seats',
      'Credit Bureau API'
    ],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 499,
    period: 'month',
    description: 'For large-scale operations',
    features: [
      'Unlimited clients',
      'AI-powered prediction',
      '24/7 dedicated support',
      'Unlimited AI summaries',
      'Unlimited users',
      'All API integrations'
    ],
    popular: false
  }
];

export function PlanSelectionModal({ open, onOpenChange }) {
  const { updatePlan } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSelectPlan = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      await updatePlan(selectedPlan);
      toast.success(`${plans.find(p => p.id === selectedPlan)?.name} plan activated!`);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to activate plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-center">Choose Your Plan</DialogTitle>
          <DialogDescription className="text-center">
            Select a plan to unlock all features and start managing your lending portfolio
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative cursor-pointer transition-all duration-300 ${selectedPlan === plan.id
                  ? 'border-2 border-primary shadow-lg shadow-primary/10'
                  : 'border-border hover:border-primary/50'
                } ${plan.popular ? 'ring-2 ring-secondary ring-offset-2' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
              data-testid={`modal-plan-${plan.id}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground">
                  Popular
                </Badge>
              )}
              {selectedPlan === plan.id && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
              )}
              <CardContent className="p-5">
                <div className="text-center mb-4">
                  <h3 className="font-heading text-lg font-bold">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1 mt-3">
                    <span className="text-3xl font-heading font-bold">${plan.price}</span>
                    <span className="text-muted-foreground text-sm">/{plan.period}</span>
                  </div>
                </div>
                <Separator className="my-3" />
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-xs">
                      <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-center mt-6">
          <Button
            size="lg"
            onClick={handleSelectPlan}
            disabled={!selectedPlan || loading}
            className="bg-primary hover:bg-primary/90 min-w-[200px]"
            data-testid="activate-plan-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                Activate Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-4">
          All plans include a 14-day free trial. Cancel anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
}
